import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { DemographicIdCode } from '~/store/boundaryGroups/boundaryGroups.repository';
import type { ChartRefId } from './mapSettingsCharts.state';

export type ChartId = Uuid;

export enum ChartType {
  BoundaryStatistics = 'boundary-statistics',
  GroupCount = 'group-count',
}

export enum ChartMetricType {
  Demographic = 'Demographic',
  LocationCount = 'LocationCount',
  SpreadsheetColumn = 'SpreadsheetColumn',
}

type BaseChartMetric = Readonly<{
  type: ChartMetricType;
}>;

export type ChartMetricSpreadsheetColumn = BaseChartMetric & Readonly<{
  type: ChartMetricType.SpreadsheetColumn;
  column: SpreadsheetColumnId;
}>;

export type ChartMetricLocationCount = BaseChartMetric & Readonly<{
  type: ChartMetricType.LocationCount;
  column?: SpreadsheetColumnId;
  columnValue?: string;
  spreadsheetId?: SpreadsheetId;
}>;

export type ChartMetricDemographic = BaseChartMetric & Readonly<{
  type: ChartMetricType.Demographic;
  demographicId: DemographicIdCode;
}>;

export type ChartMetric = ChartMetricSpreadsheetColumn | ChartMetricLocationCount | ChartMetricDemographic;

type BaseChart = Readonly<{
  id: ChartId;
  title?: string;
  shouldNormalize?: boolean;
  metrics: ReadonlyArray<ChartMetric>;
  type: ChartType;
}>;

export type ChartBoundaryStatistics = BaseChart & Readonly<{
  type: ChartType.BoundaryStatistics;
  boundaryGroupId: BoundaryGroupId;
}>;

export type ChartGroupCount = BaseChart & Readonly<{
  type: ChartType.GroupCount;
}>;

export type Chart = ChartBoundaryStatistics | ChartGroupCount;

export const isChartBoundaryStatistics = (chart: Chart): chart is ChartBoundaryStatistics => {
  return chart.type === ChartType.BoundaryStatistics;
};

export const isChartGroupCount = (chart: Chart): chart is ChartGroupCount => {
  return chart.type === ChartType.GroupCount;
};

export const isChartMetricSpreadsheetColumn = (metric: ChartMetric): metric is ChartMetricSpreadsheetColumn => {
  return metric.type === ChartMetricType.SpreadsheetColumn;
};

export const isChartMetricLocationCount = (metric: ChartMetric): metric is ChartMetricLocationCount => {
  return metric.type === ChartMetricType.LocationCount;
};

export const isChartMetricDemographic = (metric: ChartMetric): metric is ChartMetricDemographic => {
  return metric.type === ChartMetricType.Demographic;
};

// Following types will be used to store the chart metrics
// as we need to reference the columns instead of using SpreadsheetColumnId directly.
type ChartMetricWithRefColumnId = Readonly<{
  type: ChartMetricType.SpreadsheetColumn;
  column: ChartRefId;
}>;

type ChartMetricLocationCountWithRefColumnId = Readonly<{
  type: ChartMetricType.LocationCount;
  column?: ChartRefId;
  columnValue?: string;
  spreadsheetId?: ChartRefId;
}>;

type ChartRefMetric = ChartMetricWithRefColumnId | ChartMetricLocationCountWithRefColumnId | ChartMetricDemographic;

export type ChartWithRefMetrics = Omit<Chart, 'metrics'> & Readonly<{
  metrics: ReadonlyArray<ChartRefMetric>;
}>;

export const isChartMetricSpreadsheetColumnRef = (metric: ChartRefMetric): metric is ChartMetricWithRefColumnId => {
  return metric.type === ChartMetricType.SpreadsheetColumn;
};

export const isChartMetricLocationCountRef = (metric: ChartRefMetric): metric is ChartMetricLocationCountWithRefColumnId => {
  return metric.type === ChartMetricType.LocationCount;
};
